import { defer, forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap, switchMap, switchMapTo } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { ExifWrapperResult } from '../../_objects/inspection-image';
import { AnnotationImageDetail, AnnotationObjectForUpdate } from '../../_objects/inspection-image-annotation';
import { MEASUREMENT } from '../../_offline/electron.constants';
import { InspectionImageSchema } from '../../_offline/models/inspectionImage.schema';
import { UploadDataType, UploadManagementSchema } from '../../_offline/models/upload-management.schema';
import { OfflineUtilsService } from '../../_offline/services/offline-utils.service';
import { USE_IMAGE_TYPE } from '../../app.constants';
import { UtilsService } from '../_utils.service';
import { InspectionImageDbService } from '../database/inspection-image-db.service';
import { UploadManagementDbService } from '../database/upload-management-db.service';
import { FileSystemAccessService } from '../file-system-access.service';
import { AnnotationImageContractService } from '../interfaces/annotation-image-contract.service';
import { NetworkStatusService } from '../network-status.service';
import { AnnotationImageOnlineService } from '../online/annotation-image-online.service';
import { LoginUserOfflineService } from './login-user-offline.service';

@Injectable({
  providedIn: 'root'
})
export class AnnotationImageOfflineService implements AnnotationImageContractService {

  get tenantId() {
    return this.loginUserService.tenantId;
  }

  get userId() {
    return this.loginUserService.loginUser?.id;
  }

  private get isAppOnline() {
    return this.networkStatusService.isOnline;
  }

  constructor(
    private inspectionImageDbService: InspectionImageDbService,
    private loginUserService: LoginUserOfflineService,
    private uploadManagementDbService: UploadManagementDbService,
    private fileSysAccessService: FileSystemAccessService,
    private networkStatusService: NetworkStatusService,
    private annotationImageOnlineService: AnnotationImageOnlineService,
    private offlineUtilsService: OfflineUtilsService,
    private utilsService: UtilsService
  ) { }

  // AI01
  getImageAnnotations(inspectionId: string, inspectionImageId: string): Observable<AnnotationImageDetail> {
    return this.inspectionImageDbService.get(inspectionId, inspectionImageId)
      .pipe(map(schema => {
        const result = new AnnotationImageDetail();
        result.setInspectionImageSchema(schema);
        return result;
      }));
  }

  // AI02
  putImageAnnotations(updateAnnotation: AnnotationObjectForUpdate, inspectionId: string, usedImageMode: number): Observable<AnnotationImageDetail> {
    return forkJoin([
      this.inspectionImageDbService.get(inspectionId, updateAnnotation.id),
      this.uploadManagementDbService.get(this.tenantId, this.userId, updateAnnotation.id, UploadDataType.InspectionModifiedImageFile),
      this.uploadManagementDbService.get(this.tenantId, this.userId, updateAnnotation.id, UploadDataType.InspectionMeasurementImageFile)
    ])
      .pipe(switchMap(values => {
        const inspectionImage: InspectionImageSchema = values[0];
        const uploadManagementForModified: UploadManagementSchema = values[1] ? values[1] : new UploadManagementSchema(inspectionImage.id, this.tenantId, UploadDataType.InspectionModifiedImageFile, true, this.userId, inspectionId);
        const uploadManagementForMeasurement: UploadManagementSchema = values[2] ? values[2] : new UploadManagementSchema(inspectionImage.id, this.tenantId, UploadDataType.InspectionMeasurementImageFile, true, this.userId, inspectionId);
        const processes: Array<Observable<any>> = new Array<Observable<any>>();

        inspectionImage.modifiedItems = (usedImageMode === USE_IMAGE_TYPE.RETOUCHED) ? (updateAnnotation?.modifiedItems ? JSON.stringify(updateAnnotation.modifiedItems) : null) : inspectionImage.modifiedItems;
        inspectionImage.measurementItems = (usedImageMode === USE_IMAGE_TYPE.REMEASUREMENT) ? (updateAnnotation?.measurementItems ? JSON.stringify(updateAnnotation.measurementItems) : null) : inspectionImage.measurementItems;
        inspectionImage.usedImageMode = this.getUsedImageMode(updateAnnotation);
        inspectionImage.schemaVersion = updateAnnotation.schemaVersion;

        if (!inspectionImage.modifiedItems) {
          // null 削除の場合は削除判定 オフラインデータからも削除する
          inspectionImage.modifiedWidth = null;
          inspectionImage.modifiedHeight = null;
          processes.push(
            forkJoin([
              defer(() => this.fileSysAccessService.deleteFileForce(this.genModifiedFilePath(inspectionImage.localFilePath))),
              defer(() => this.fileSysAccessService.deleteFileForce(this.genModifiedFilePath(inspectionImage.thumbFilePath)))
            ])
          );
        }
        if (!inspectionImage.measurementItems) {
          // null 削除の場合は削除判定 オフラインデータからも削除する
          inspectionImage.measurementWidth = null;
          inspectionImage.measurementHeight = null;
          processes.push(
            forkJoin([
              defer(() => this.fileSysAccessService.deleteFileForce(this.genMeasurementFilePath(inspectionImage.localFilePath))),
              defer(() => this.fileSysAccessService.deleteFileForce(this.genMeasurementFilePath(inspectionImage.thumbFilePath)))
            ])
          );
        }

        uploadManagementForModified.isDeleted = !inspectionImage.modifiedItems;
        processes.push(this.uploadManagementDbService.put(uploadManagementForModified));

        uploadManagementForMeasurement.isDeleted = !inspectionImage.measurementItems;
        processes.push(this.uploadManagementDbService.put(uploadManagementForMeasurement));

        return forkJoin(processes)
          .pipe(
            mergeMap(() => this.inspectionImageDbService.put(inspectionImage))
          );
      }))
      .pipe(switchMap(schema => {
        if (!this.isAppOnline) {
          return of(schema);
        }

        return this.annotationImageOnlineService.putImageAnnotations(updateAnnotation, inspectionId, usedImageMode)
          .pipe(switchMap(res => {
            return this.inspectionImageDbService.put(schema);
          }));
      }))
      .pipe(map(schema => {
        const result = new AnnotationImageDetail();
        result.setInspectionImageSchema(schema);
        return result;
      }));
  }

  // AI03
  putModifiedImage(inspectionId: string, inspectionImageId: string, file: File, usedImageMode?: number): Observable<AnnotationImageDetail> {
    let inspectionImage: InspectionImageSchema = null;
    const pathSeparator = this.fileSysAccessService.getSystemPathSeparator();
    return this.inspectionImageDbService.get(inspectionId, inspectionImageId)
      .pipe(
        mergeMap(value => {
          inspectionImage = value;

          return forkJoin([
            defer(() => this.fileSysAccessService.getFileFullPath(inspectionImage.localFilePath)),
            defer(() => this.fileSysAccessService.getFileFullPath(inspectionImage.thumbFilePath))
          ]);
        }),
        mergeMap(x => {
          const filePath: string = x[0];
          const outputThumbnailFilePath: string = x[1];
          const delimitedFilePath: Array<string> = filePath.split(/\\|\//);
          delimitedFilePath.pop();
          const outputJsonFilePath: string = delimitedFilePath.join(pathSeparator) + pathSeparator + inspectionImage.id + '.json';

          return this.offlineUtilsService.readExifInfo(filePath.replace(/\//g, pathSeparator), outputJsonFilePath, inspectionImage.isDataPlate, inspectionImage.mimeType, outputThumbnailFilePath);
        }),
        mergeMap(value => {
          const processes: Array<Observable<any>> = new Array<Observable<any>>();
          processes.push(this.inspectionImageDbService.put(inspectionImage));
          if (usedImageMode === USE_IMAGE_TYPE.RETOUCHED) {
            processes.push(
              this.updateModifiedSrc(inspectionImage.localFilePath, file)
                .pipe(
                  mergeMap(() => this.updateModifiedThumbnail(inspectionImage))
                )
            );
          } else if (usedImageMode === USE_IMAGE_TYPE.REMEASUREMENT) {
            processes.push(
              this.updateMeasurementSrc(inspectionImage.localFilePath, file)
                .pipe(
                  mergeMap(() => this.updateMeasurementThumbnail(inspectionImage))
                )
            );
          }

          return forkJoin(processes);
        }),
        mergeMap(values => {
          // 編集画像のwidth, heightを取得する
          inspectionImage.modifiedWidth = (usedImageMode === USE_IMAGE_TYPE.RETOUCHED) ? values[1]?.width : inspectionImage.modifiedWidth;
          inspectionImage.modifiedHeight = (usedImageMode === USE_IMAGE_TYPE.RETOUCHED) ? values[1]?.height : inspectionImage.modifiedHeight;
          inspectionImage.measurementWidth = (usedImageMode === USE_IMAGE_TYPE.REMEASUREMENT) ? values[1]?.width : inspectionImage.measurementWidth;
          inspectionImage.measurementHeight = (usedImageMode === USE_IMAGE_TYPE.REMEASUREMENT) ? values[1]?.height : inspectionImage.measurementHeight;

          if (!this.isAppOnline) {
            return this.inspectionImageDbService.put(inspectionImage);
          }

          return this.annotationImageOnlineService.putModifiedImage(inspectionId, inspectionImageId, file, usedImageMode)
            .pipe(
              mergeMap(res => {
                const schema: InspectionImageSchema = values[0] as InspectionImageSchema;
                schema.modifiedWidth = inspectionImage.modifiedWidth;
                schema.modifiedHeight = inspectionImage.modifiedHeight;
                schema.measurementWidth = inspectionImage.measurementWidth;
                schema.measurementHeight = inspectionImage.measurementHeight;
                return this.inspectionImageDbService.put(schema);
              })
            );
        }),
        switchMapTo(this.getImageAnnotations(inspectionId, inspectionImageId))
      );
  }

  // オフラインデータの編集画像情報を更新する
  private updateModifiedSrc(filePath: string, file: File): Observable<boolean> {
    const modifiedFilePath = this.genModifiedFilePath(filePath);
    return defer(() => this.fileSysAccessService.saveFile(file, modifiedFilePath));
  }

  /**
   * 編集済み画像のサムネイルを更新する。
   *
   * @param inspectionImage InspectionImageSchema。
   * @returns ExifWrapperResult。
   */
  private updateModifiedThumbnail(inspectionImage: InspectionImageSchema): Observable<ExifWrapperResult> {
    return forkJoin([
      defer(() => this.fileSysAccessService.getFileFullPath(this.genModifiedFilePath(inspectionImage.localFilePath))),
      defer(() => this.fileSysAccessService.getFileFullPath(this.genModifiedFilePath(inspectionImage.thumbFilePath)))
    ])
      .pipe(
        mergeMap(value => {
          const modifiedFilePath: string = value[0];
          const modifiedThumbnailFilePath: string = value[1];
          return this.offlineUtilsService.readExifInfo(modifiedFilePath, this.utilsService.changeExtension(modifiedFilePath, 'json'), inspectionImage.isDataPlate, inspectionImage.mimeType, modifiedThumbnailFilePath);
        })
      );
  }

  private genModifiedFilePath(filePath: string) {
    const filePathParts = filePath.split('/');
    const _fileName = 'modified_' + filePathParts[filePathParts.length - 1];
    const pathSeparator = this.fileSysAccessService.getSystemPathSeparator();
    filePathParts.splice(filePathParts.length - 1, 0, 'modified');
    filePathParts[filePathParts.length - 1] = _fileName;
    return filePathParts.join(pathSeparator);
  }

  /**
   * オフラインデータの計測画像情報を更新する。
   *
   * @param filePath ファイルパス。
   * @param file 画像ファイル。
   * @returns 保存実行結果。
   */
  private updateMeasurementSrc(filePath: string, file: File): Observable<boolean> {
    const measurementFilePath: string = this.genMeasurementFilePath(filePath);
    return defer(() => this.fileSysAccessService.saveFile(file, measurementFilePath));
  }

  /**
   * 計測済み画像のサムネイルを更新する。
   *
   * @param inspectionImage InspectionImageSchema。
   * @returns ExifWrapperResult。
   */
  private updateMeasurementThumbnail(inspectionImage: InspectionImageSchema): Observable<ExifWrapperResult> {
    return forkJoin([
      defer(() => this.fileSysAccessService.getFileFullPath(this.genMeasurementFilePath(inspectionImage.localFilePath))),
      defer(() => this.fileSysAccessService.getFileFullPath(this.genMeasurementFilePath(inspectionImage.thumbFilePath)))
    ])
      .pipe(
        mergeMap(value => {
          const modifiedFilePath: string = value[0];
          const modifiedThumbnailFilePath: string = value[1];
          return this.offlineUtilsService.readExifInfo(modifiedFilePath, this.utilsService.changeExtension(modifiedFilePath, 'json'), inspectionImage.isDataPlate, inspectionImage.mimeType, modifiedThumbnailFilePath);
        })
      );
  }

  /**
   * 計測画像のファイルパスを生成する。
   *
   * @param filePath 画像のファイルパス。
   * @returns 計測画像のファイルパス。
   */
  private genMeasurementFilePath(filePath: string): string {
    const filePathParts: Array<string> = filePath.split(/\\|\//);
    const measurementFileName: string = MEASUREMENT.FILE_PREFIX + filePathParts.pop();
    filePathParts.push(MEASUREMENT.DIRECTORY_NAME);
    filePathParts.push(measurementFileName);
    return filePathParts.join('\\');
  }

  /**
   * UsedImageMode を取得する。
   * Backend の AnnotationImageManagementService.SetUsedImageMode と同様の処理。
   *
   * @param annotationObjectForUpdate AnnotationObjectForUpdate。
   * @returns USE_IMAGE_TYPE。
   */
  private getUsedImageMode(annotationObjectForUpdate: AnnotationObjectForUpdate): USE_IMAGE_TYPE {
    if (!annotationObjectForUpdate.modifiedItems && !annotationObjectForUpdate.measurementItems) {
      return USE_IMAGE_TYPE.ORIGINAL;
    }

    if (annotationObjectForUpdate.modifiedItems) {
      return USE_IMAGE_TYPE.RETOUCHED;
    } else {
      return USE_IMAGE_TYPE.REMEASUREMENT;
    }
  }

}
